<script setup lang="ts">
  import { computed, ref, type HTMLAttributes } from 'vue'
  import { globalUtils, useFormatting } from '@gat-ship/utils'
  import { Input } from '../input'

  const props = withDefaults(
    defineProps<{
      modelValue?: number
      /** Number of decimals. `0` (default) = integer only. */
      numberOfDecimals?: number
      /**
       * Always show trailing zeros up to `numberOfDecimals`.
       * When `false`, `1000` stays `1 000` instead of `1 000,00`.
       */
      fixedDecimals?: boolean
      min?: number
      max?: number
      id?: string
      placeholder?: string
      disabled?: boolean
      dataTestid?: string
      ariaInvalid?: boolean
      class?: HTMLAttributes['class']
    }>(),
    {
      numberOfDecimals: 0,
      fixedDecimals: false
    }
  )

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number | undefined): void
    (e: 'blur'): void
    (e: 'focus'): void
  }>()

  const { formatNumber } = useFormatting()

  const isFocused = ref(false)
  const rawInput = ref('')

  const decimalSeparator = computed(() => {
    const locale = globalUtils.date.currentNumberFormat.value
    const parts = new Intl.NumberFormat(locale).formatToParts(1.1)
    return parts.find((p) => p.type === 'decimal')?.value ?? '.'
  })

  const groupSeparator = computed(() => {
    const locale = globalUtils.date.currentNumberFormat.value
    const parts = new Intl.NumberFormat(locale).formatToParts(1000)
    return parts.find((p) => p.type === 'group')?.value ?? ''
  })

  function parseLocaleNumber(input: string): number | undefined {
    if (input === '' || input === undefined || input === null) return undefined
    const cleaned = input
      .replace(/\s/g, '')
      .replace(new RegExp(`\\${groupSeparator.value}`, 'g'), '')
      .replace(decimalSeparator.value, '.')
    if (cleaned === '' || cleaned === '-') return undefined
    const n = Number(cleaned)
    if (Number.isNaN(n)) return undefined
    if (props.numberOfDecimals > 0) {
      return Number(n.toFixed(props.numberOfDecimals))
    }
    return Math.trunc(n)
  }

  function formatDisplay(value: number | undefined): string {
    if (value === undefined || value === null) return ''
    if (typeof value !== 'number' || Number.isNaN(value)) return ''
    return formatNumber(value, {
      minDecimals: props.fixedDecimals ? props.numberOfDecimals : 0,
      maxDecimals: props.numberOfDecimals
    })
  }

  function displayValue(value: number | undefined): string {
    if (isFocused.value) return rawInput.value
    return formatDisplay(value)
  }

  function isValidInput(value: string): boolean {
    if (value === '') return true
    const stripped = value.replace(/\s/g, '')
    if (stripped === '' || stripped === '-') return true
    const sep = decimalSeparator.value
    const escapedSep = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const pattern =
      props.numberOfDecimals > 0
        ? new RegExp(`^-?\\d*${escapedSep}?\\d{0,${props.numberOfDecimals}}$`)
        : /^-?\d*$/
    return pattern.test(stripped)
  }

  function handleBeforeInput(event: InputEvent) {
    if (!event.data) return
    const target = event.target as HTMLInputElement
    const nextValue =
      target.value.slice(0, target.selectionStart ?? undefined) +
      event.data +
      target.value.slice(target.selectionEnd ?? undefined)
    if (!isValidInput(nextValue)) {
      event.preventDefault()
    }
  }

  function handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value
    rawInput.value = value
    const parsed = parseLocaleNumber(value)
    emit('update:modelValue', parsed)
  }

  function handleFocus(value: number | undefined) {
    isFocused.value = true
    rawInput.value = formatDisplay(value)
    emit('focus')
  }

  function handleBlur() {
    isFocused.value = false
    const v = props.modelValue
    if (v !== undefined && v !== null) {
      const clamped = clamp(v)
      if (clamped !== v) emit('update:modelValue', clamped)
    }
    emit('blur')
  }

  function clamp(value: number): number {
    if (props.min !== undefined && value < props.min) return props.min
    if (props.max !== undefined && value > props.max) return props.max
    return value
  }

  function handlePaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text') ?? ''
    const target = event.target as HTMLInputElement
    const nextValue =
      target.value.slice(0, target.selectionStart ?? undefined) +
      pasted +
      target.value.slice(target.selectionEnd ?? undefined)
    if (!isValidInput(nextValue)) {
      event.preventDefault()
    }
  }
</script>

<template>
  <Input
    :id="id"
    type="text"
    :inputmode="numberOfDecimals > 0 ? 'decimal' : 'numeric'"
    :model-value="displayValue(modelValue)"
    :placeholder="placeholder"
    :data-testid="dataTestid"
    :class="props.class"
    :disabled="disabled"
    :aria-invalid="ariaInvalid || undefined"
    @beforeinput="handleBeforeInput"
    @input="handleInput"
    @focus="handleFocus(modelValue)"
    @blur="handleBlur"
    @paste="handlePaste"
  />
</template>
